import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FEEDBACK_QUESTIONS, type FeedbackQuestion } from '../../config/feedback-form';
import { getAppVersion } from '../../utils/changelog';
import { submitFeedback } from '../../services/Feedback';
import { getOwnUser } from '../../services/User';
import { useBoundStore } from '../../store';
import { useI18n } from '../../i18n';

const RatingInput: React.FC<{
  question: FeedbackQuestion;
  value: number | undefined;
  onChange: (value: number) => void;
}> = ({ question, value, onChange }) => {
  const max = question.max || 5;
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-0.5 hover:scale-110 transition-transform"
        >
          <Star
            className={`h-6 w-6 ${
              value && star <= value
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const FeedbackPage: React.FC = () => {
  const { t } = useI18n();
  const f = t.feedback;
  const labelFor = (q: FeedbackQuestion) => f.questions[q.id]?.label ?? q.label;
  const descriptionFor = (q: FeedbackQuestion) => f.questions[q.id]?.description ?? q.description;
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscriptionExtended, setSubscriptionExtended] = useState(false);
  const setUser = useBoundStore((state) => state.setUser);

  const updateResponse = (id: string, value: any) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    const missingRequired = FEEDBACK_QUESTIONS.filter(
      (q) => q.required && (responses[q.id] === undefined || responses[q.id] === ''),
    );

    if (missingRequired.length > 0) {
      toast.error(f.missingRequired(missingRequired.map(labelFor).join(', ')));
      return;
    }

    setSubmitting(true);
    const result = await submitFeedback({
      formVersion: getAppVersion(),
      responses,
    });
    setSubmitting(false);

    if (result.success) {
      if (result.data?.subscriptionExtended) {
        toast.success(f.subscriptionExtendedToast);
        setSubscriptionExtended(true);
        // Refresh user data to update subscription status
        const userResult = await getOwnUser();
        if (userResult.success && userResult.data) {
          setUser(userResult.data);
        }
      } else {
        toast.success(f.thanksToast);
      }
      setSubmitted(true);
    } else {
      toast.error(result.errors?.[0]?.title || f.submitFailed);
    }
  };

  const handleReset = () => {
    setResponses({});
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-6">{f.title}</h1>
        <Card>
          <CardHeader>
            <CardTitle>{f.thankYou}</CardTitle>
            <CardDescription>
              {subscriptionExtended
                ? f.submittedExtended
                : f.submitted}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleReset}>{f.submitAnother}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">{f.title}</h1>
      <Card>
        <CardHeader>
          <CardTitle>{f.heading}</CardTitle>
          <CardDescription>
            {f.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {FEEDBACK_QUESTIONS.map((question) => (
              <div key={question.id} className="space-y-2">
                <Label>
                  {labelFor(question)}
                  {question.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {question.description && (
                  <p className="text-sm text-muted-foreground">{descriptionFor(question)}</p>
                )}

                {question.type === 'rating' && (
                  <RatingInput
                    question={question}
                    value={responses[question.id]}
                    onChange={(val) => updateResponse(question.id, val)}
                  />
                )}

                {question.type === 'text' && (
                  <Textarea
                    placeholder={f.responsePlaceholder}
                    value={responses[question.id] || ''}
                    onChange={(e) => updateResponse(question.id, e.target.value)}
                  />
                )}

                {question.type === 'select' && question.options && (
                  <Select
                    value={responses[question.id] || ''}
                    onValueChange={(val) => updateResponse(question.id, val)}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder={f.selectPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {question.options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {f.options[option] ?? option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}

            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? f.submitting : f.submit}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackPage;
